import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Send, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import WaitlistModal from '@/components/WaitlistModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSuccess(true);
      toast.success('Message sent successfully');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#EDECEA] flex flex-col" data-testid="contact-page">
      <Navigation onWaitlistClick={() => setIsWaitlistOpen(true)} />

      <div className="flex-1 max-w-2xl mx-auto px-6 pt-32 pb-20 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0F1B2D] mb-4">
            Contact the Association
          </h1>
          <p className="text-[#4A4640] font-medium mb-12">
            For membership enquiries, press matters, or general questions.
          </p>

          {isSuccess ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inst-card rounded-lg p-12 text-center"
            >
              <CheckCircle className="w-16 h-16 text-[#C4A35A] mx-auto mb-6" />
              <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0F1B2D] mb-3">
                Message Received
              </h2>
              <p className="text-[#4A4640] font-medium mb-8">
                The Association will respond within 2–3 working days.
              </p>
              <Link
                to="/"
                className="btn-ainaa-secondary inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold uppercase tracking-wider"
              >
                <ArrowLeft size={16} />
                Back to AINAA
              </Link>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#9B9589] font-bold mb-2">
                    Name *
                  </label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    className="input-ainaa w-full h-12 px-4"
                    disabled={isLoading}
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#9B9589] font-bold mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="input-ainaa w-full h-12 px-4"
                    disabled={isLoading}
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#9B9589] font-bold mb-2">
                  Subject *
                </label>
                <Input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Regarding..."
                  className="input-ainaa w-full h-12 px-4"
                  disabled={isLoading}
                  data-testid="contact-subject-input"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#9B9589] font-bold mb-2">
                  Message *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Your message to the Association..."
                  rows={6}
                  className="input-ainaa w-full px-4 py-4 resize-none"
                  disabled={isLoading}
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-ainaa-primary w-full py-4 text-sm font-semibold flex items-center justify-center gap-2 uppercase tracking-wider"
                data-testid="contact-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send size={16} />
                  </>
                )}
              </button>
            </form>
          )}
        </motion.div>
      </div>

      <Footer />
      <WaitlistModal isOpen={isWaitlistOpen} onClose={() => setIsWaitlistOpen(false)} />
    </div>
  );
};

export default Contact;
